import React, { useState, useEffect } from 'react';
import Modal from '@omilia/modal';
import Dropzone from '@omilia/dropzone';
import Button, { ButtonGroup } from '@omilia/button';
import { SSelect } from 'components/styled';
import { Form, Field } from 'react-final-form';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import CSVPreview from 'components/CSVPreview';
import {
  saveCSV,
  clearCSV,
  uploadCSV,
  cancelSaveCSV,
  cancelUploadCSV,
} from 'store/services/csv/actions';
import * as selectors from 'store/services/csv/selectors';
import {
  FORMATS as CSV_FORMATS,
  SEPARATORS as CSV_SEPARATORS,
} from 'constants/csvOptions';
import { SModalBody, SForm, SFormGroup, SModalFooter } from './styled';
import RadioFieldGroup from './components/RadioFormGroup';
import Alert from './components/Alert';
import validate from './utils/validate';
import { Props, FieldProps } from './types';

const UploadCSVFileModal = ({
  dictionary,
  onClose,
  currentCSV,
  saveCSV,
  clearCSV,
  uploadCSV,
  cancelSaveCSV,
  cancelUploadCSV,
  errors,
}: Props) => {
  const [loadingSave, setLoadingSave] = useState(false);
  const isPreview = !isEmpty(currentCSV);

  useEffect(() => clearCSV, [clearCSV]);

  const handleUpload = async ({ file, encoding, separator }: FieldProps) => {
    const { id, name } = dictionary;
    const { value: encodingValue } = encoding || { value: '' };

    if (id && name) {
      if (file[0]) {
        await uploadCSV({
          name,
          dictionary: id,
          uploaded_file: file[0],
          file_separator: separator,
          file_encoding: encodingValue,
        });
      }
    }
  };

  const handleSave = async () => {
    const { id } = currentCSV;

    setLoadingSave(true);

    if (id) {
      try {
        await saveCSV(id);
        onClose(true);
      } finally {
        setLoadingSave(false);
      }
    }
  };

  const handleClose = () => {
    cancelSaveCSV();
    cancelUploadCSV();
    onClose();
  };

  return (
    <Modal
      open
      shouldCloseOnBackdropClick
      shouldCloseOnEscPress
      glyph="csv-files"
      subtitle={
        isPreview
          ? 'Preview table. If you are ok with what you see proceed'
          : 'Start by selecting a .csv format file'
      }
      title="Upload CSV file"
      onClose={() => onClose()}
    >
      <SModalBody data-test="upload-csv-modal-body">
        <Alert errors={errors} />
        <Form
          initialValues={{
            encoding: { label: CSV_FORMATS[0], value: CSV_FORMATS[0] },
            separator: CSV_SEPARATORS[0].value,
          }}
          validate={validate}
          onSubmit={handleUpload}
        >
          {({ handleSubmit, invalid, submitting }) => (
            <SForm onSubmit={handleSubmit}>
              {isPreview ? (
                <CSVPreview csv={currentCSV} />
              ) : (
                <>
                  <div>
                    <Field name="file">
                      {props => (
                        <Dropzone
                          showFileList
                          accept=".csv"
                          description="Drop .csv here to import"
                          disabled={submitting}
                          label="File"
                          multiple={false}
                          {...props.input}
                          {...props}
                        />
                      )}
                    </Field>
                  </div>
                  <SFormGroup>
                    <Field
                      label="Select file enconding"
                      name="encoding"
                      placeholder=""
                    >
                      {props => (
                        <SSelect
                          options={CSV_FORMATS.map((value: string) => ({
                            label: value,
                            value,
                          }))}
                          {...props.input}
                          {...props}
                        />
                      )}
                    </Field>
                    <RadioFieldGroup
                      disabled={submitting}
                      items={CSV_SEPARATORS}
                    />
                  </SFormGroup>
                </>
              )}
              <SModalFooter>
                <ButtonGroup>
                  <Button
                    color="secondary"
                    data-test="close-button"
                    variant="text"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  {isPreview ? (
                    <>
                      <Button
                        color="primary"
                        data-test="reselect-button"
                        disabled={submitting}
                        variant="contained"
                        onClick={clearCSV}
                      >
                        Change selections
                      </Button>
                      <Button
                        color="primary"
                        data-test="save-button"
                        loading={loadingSave}
                        variant="contained"
                        onClick={handleSave}
                      >
                        Save Changes
                      </Button>
                    </>
                  ) : (
                    <Button
                      color="primary"
                      data-test="upload-button"
                      disabled={invalid}
                      loading={submitting || loadingSave}
                      type="submit"
                      variant="contained"
                    >
                      Upload
                    </Button>
                  )}
                </ButtonGroup>
              </SModalFooter>
            </SForm>
          )}
        </Form>
      </SModalBody>
    </Modal>
  );
};

const mapStateToProps = (state: unknown) => ({
  errors: selectors.getUploadingErrorMessage(state),
  currentCSV: selectors.getCurrentCSV(state),
});

const mapDispatchToProps = {
  saveCSV,
  clearCSV,
  uploadCSV,
  cancelSaveCSV,
  cancelUploadCSV,
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadCSVFileModal);
